import React from 'react';
import PropTypes from 'prop-types';
import { LuListFilter, LuSearch, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { GLASS_FIELD } from '../../../components/ui/glass';
import { useTranslation } from '../../../i18n/LanguageContext';
import { columnLabel, getFilterState, getFilterValueLabel } from '../employeeColumns';
import ColumnMenu from './ColumnMenu';

const CHIP_STATES = {
  applied: 'tw-border-teal-500/25 tw-bg-teal-500/10 tw-text-teal-800 dark:tw-border-teal-400/20 dark:tw-bg-teal-400/10 dark:tw-text-teal-200',
  pending: 'tw-border-dashed tw-border-amber-500/40 tw-bg-amber-400/10 tw-text-amber-800 dark:tw-border-amber-400/30 dark:tw-text-amber-200',
};

/**
 * Search, status switch, column chooser and the active column filters.
 * `compact` (the card list on narrow screens): stacked, and a "Filtreler"
 * button opens the column filters, since there are no table headers.
 */
const EmployeeToolbar = ({ searchTerm, onSearchChange, filters, onFilterChange, onClearFilters, columns, compact, onOpenFilters }) => {
  const { t } = useTranslation();
  const isWide = useMediaQuery('(min-width: 640px)');
  const statusOptions = [
    { value: '1', label: t('employees.status.active') },
    { value: '0', label: t('employees.status.inactive') },
    { value: '', label: t('employees.status.all') },
  ];
  const statusValue = filters.Active === undefined || filters.Active === null ? '' : String(filters.Active);
  const searchTooShort = searchTerm.length > 0 && searchTerm.length < 3;
  const columnFilters = Object.keys(filters).filter((key) => key !== 'Active' && getFilterState(key, filters[key]) !== 'off');
  // Room on the right of the field for the hint and the clear button
  let searchPadding = 'tw-pr-4';
  if (searchTooShort) searchPadding = 'tw-pr-40';
  else if (searchTerm) searchPadding = 'tw-pr-10';

  return (
    <div className="tw-space-y-3 tw-p-3 sm:tw-p-4">
      <div className={`tw-flex tw-flex-col tw-gap-3 ${compact ? '' : 'md:tw-flex-row md:tw-items-center'}`}>
        <div className={`tw-relative tw-min-w-0 ${compact ? '' : 'md:tw-max-w-md md:tw-flex-1'}`}>
          <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && searchTerm) {
                event.preventDefault();
                onSearchChange('');
              }
            }}
            placeholder={compact ? t('employees.toolbar.searchPlaceholderShort') : t('employees.toolbar.searchPlaceholder')}
            aria-label={t('employees.toolbar.searchLabel')}
            enterKeyHint="search"
            className={`${GLASS_FIELD} tw-pl-10 ${searchPadding}`}
          />
          <div className="tw-absolute tw-right-1.5 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-items-center tw-gap-1">
            {searchTooShort && (
              <span className="tw-pointer-events-none tw-whitespace-nowrap tw-rounded-full tw-bg-amber-400/15 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-amber-700 dark:tw-text-amber-300">
                {t('employees.toolbar.minChars')}
              </span>
            )}
            {searchTerm && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                aria-label={t('employees.toolbar.clearSearch')}
                className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
              >
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            )}
          </div>
        </div>

        <div className={`tw-flex tw-items-center tw-justify-between tw-gap-2 ${compact ? '' : 'md:tw-ml-auto md:tw-justify-end'}`}>
          <SegmentedControl
            options={statusOptions}
            value={statusValue}
            onChange={(value) => onFilterChange('Active', value)}
            layoutId="employee-status-filter"
            label={t('employees.toolbar.statusLabel')}
            size={isWide && !compact ? 'md' : 'sm'}
          />
          {compact ? (
            <Button
              onClick={onOpenFilters}
              aria-label={columnFilters.length ? t('employees.toolbar.filtersOpen', { count: columnFilters.length }) : t('employees.toolbar.filters')}
            >
              <LuListFilter aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-500 dark:tw-text-slate-400" />
              {t('employees.toolbar.filters')}
              {columnFilters.length > 0 && (
                <span className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-teal-500 tw-px-1.5 tw-text-[11px] tw-font-bold tw-leading-none tw-text-white tw-tabular-nums">
                  {columnFilters.length}
                </span>
              )}
            </Button>
          ) : (
            <ColumnMenu {...columns} />
          )}
        </div>
      </div>

      {columnFilters.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{t('employees.toolbar.filters')}</span>
          {columnFilters.map((key) => {
            const state = getFilterState(key, filters[key]);
            const label = columnLabel(t, key);
            return (
              <span
                key={key}
                title={state === 'pending' ? t('employees.toolbar.pendingFilter') : undefined}
                className={`tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-py-1 tw-pl-3 tw-pr-1 tw-text-xs ${CHIP_STATES[state]}`}
              >
                <span className="tw-font-medium tw-opacity-75">{label}:</span>
                <span className="tw-max-w-[12rem] tw-truncate tw-font-semibold">{getFilterValueLabel(t, key, filters[key])}</span>
                <button
                  type="button"
                  onClick={() => onFilterChange(key, '')}
                  aria-label={t('employees.toolbar.removeFilter', { label })}
                  className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors hover:tw-bg-slate-900/10 dark:hover:tw-bg-white/15"
                >
                  <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={onClearFilters}
            className="tw-rounded-full tw-bg-transparent tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-800 dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.07] dark:hover:tw-text-white"
          >
            {t('employees.toolbar.clearAll')}
          </button>
        </div>
      )}
    </div>
  );
};

EmployeeToolbar.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  onOpenFilters: PropTypes.func,
  columns: PropTypes.shape({
    visibleColumns: PropTypes.objectOf(PropTypes.bool).isRequired,
    exitDateAutoHidden: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    onShowAll: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
  }).isRequired,
};

export default EmployeeToolbar;
